import React from "react";
import Modal from "../../../components/common/Modal";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";
import { ReactComponent as SuccessIcon } from "../../../assets/images/svg/successIcon.svg";
import { ReactComponent as ErrorIcon } from "../../../assets/images/svg/errorIcon.svg";
import { ReactComponent as WarningIcon } from "../../../assets/images/svg/warningIcon.svg";
import { getArabicOrdinalFemale } from "../../../setup/util/arabicOrdinalName";
import OutlinedButton from "../../../components/common/OutlinedButton";

interface IPublishResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  isSuccess: boolean;
  milestones?: { name: string; index: number }[];
  errors?: Record<string, { index: number; errors: string[] }>;
}

const PublishResultModal: React.FC<IPublishResultModalProps> = ({
  isOpen,
  onClose,
  isSuccess,
  milestones = [],
  errors = {},
}) => {
  const { t } = useTranslation();

  // TODO: Should we add the milestones that are not relevant too?

  return (
    <Modal
      visible={isOpen}
      onCancel={onClose}
      size="small"
      customModalClassName={styles.publishResultModal}>
      <div className={styles.publishResultContainer}>
        <div className={styles.modalHeader}>
          {isSuccess ? <SuccessIcon /> : <ErrorIcon />}
          <h1 className={styles.modalTitle}>
            {isSuccess ? t("lbl_publish_success") : t("lbl_publish_error")}
          </h1>
          <p className={styles.modalDescription}>
            {isSuccess ? "" : t("lbl_publish_error_description")}
          </p>
        </div>

        <div className={styles.modalContent}>
          {isSuccess ? (
            <div className={styles.successContent}>
              {milestones.length > 0 && (
                <div className={styles.milestonesList}>
                  <ul className={styles.milestonesItems}>
                    {[...milestones]
                      .sort((a, b) => a.index - b.index)
                      .map((milestone, arrayIndex, arr) => (
                        <React.Fragment key={`milestone-${arrayIndex}`}>
                          <li className={styles.milestoneItem}>
                            <SuccessIcon />
                            <span>
                              {`${t("lbl_milestone")} ${getArabicOrdinalFemale(
                                milestone.index + 1
                              )} : ${milestone.name}`}
                            </span>
                          </li>
                          {arrayIndex !== arr?.length - 1 && (
                            <div
                              style={{
                                width: "100%",
                                background: "#EEF0F5",
                                height: "1px",
                              }}
                            />
                          )}
                        </React.Fragment>
                      ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className={styles.errorContent}>
              {Object.keys(errors || {}).length > 0 && (
                <div className={styles.errorsList}>
                  {Object.entries(errors || {})
                    .sort(([, a], [, b]) => a.index - b.index)
                    .map(
                      (
                        [milestoneName, milestoneData],
                        arrayIndex,
                        milestonesArray
                      ) => (
                        <React.Fragment key={`error-${arrayIndex}`}>
                          <div className={styles.milestoneErrorGroup}>
                            <WarningIcon
                              style={{ width: "20px", height: "20px" }}
                            />
                            <h3 className={styles.milestoneErrorTitle}>
                              {`${t("lbl_milestone")} ${getArabicOrdinalFemale(
                                milestoneData.index + 1
                              )} : ${milestoneName}`}
                              <ul className={styles.errorItems}>
                                {milestoneData.errors.map(
                                  (error, errorIndex) => (
                                    <li
                                      key={`${arrayIndex}-${errorIndex}`}
                                      className={styles.errorItem}>
                                      <span>{error}</span>
                                    </li>
                                  )
                                )}
                              </ul>
                            </h3>
                          </div>
                          {arrayIndex !== milestonesArray?.length - 1 && (
                            <div
                              style={{
                                width: "100%",
                                background: "#EEF0F5",
                                height: "1px",
                              }}
                            />
                          )}
                        </React.Fragment>
                      )
                    )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className={styles.modalFooter}>
          <OutlinedButton
            onClick={onClose}
            style={{
              width: "100%",
              height: "52px",
              color: "#0D44AD",
              background: "#F3F6FC",
              border: "none",
              borderRadius: "8px",
              fontSize: "16px",
              fontWeight: 600,
            }}>
            {t("closed")}
          </OutlinedButton>
        </div>
      </div>
    </Modal>
  );
};

export default PublishResultModal;
